/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { EventEmitter, Inject, Injectable, InjectionToken, Type } from '@angular/core';

import { REGION_CODE } from '@gh/config';
import { TranslationService } from '@gh/core-mls';
import { DialogService } from '@gh/core-ui';
import { coalesce, toBoolean } from '@gh/core-util';
import { debounceTime$, filter$, switchMap$, takeUntil$ } from '@gh/rx/operators';

import {
  ColDef,
  ColGroupDef,
  Column,
  GridApi,
  GridOptions,
  SortChangedEvent,
} from 'ag-grid-community';

import { GRID_EVENT } from '../grid-public-events';
import {
  constant,
  difference,
  extend,
  filter,
  find,
  first,
  flatten,
  forEach,
  fromPairs,
  isFunction,
  isNil,
  noop,
  over,
} from 'lodash';
import { Observable, Subject } from 'rxjs';
import { isColDef, MIN_COLUMN_WIDTH, GridColumnComponent } from '../components';
import { GridDefinition } from './grid-definition';
import {
  byColumn,
  fromGridDirection,
  getGridColumn,
  GridLayout,
  ManagedGridLayout,
  onlyInvisible,
  onlyVisible,
  setGridColumnVisibility,
  setGridColumnWidth,
} from './grid-layout';
import { GridLayoutService } from './grid-layout.service';

const CELL_FIRST_COLUMN_CLASS = 'cell--first-column';

const DEFAULT_PAGE_SIZE = 20;
const SAVE_DEBOUNCE_TIME = 300;

const getFirstColumn = (colDef: ColDef | ColGroupDef): Maybe<ColDef> => {
  if (isColDef(colDef)) {
    return colDef;
  } else {
    let childColDef: ColDef | ColGroupDef = colDef;
    while (childColDef && !isColDef(childColDef)) {
      childColDef = childColDef.children[0];
    }
    return <Maybe<ColDef>>childColDef;
  }
};

const getColumnId = (colDef: ColDef) => colDef.colId || colDef.field;

const forEachColDef = (defs: (ColDef | ColGroupDef)[], fn: ProcedureFn<ColDef>) => {
  forEach(defs, (def) => {
    if (isColDef(def)) {
      fn(def);
    } else {
      forEachColDef(def.children, fn);
    }
  });
};

export interface GridSettingsDialogConfig {
  layout: GridLayout;
  translate: (name: string) => string;
  onSave: (layout: GridLayout) => Observable<GridLayout>;
}

export const GRID_SETTINGS_DIALOG = new InjectionToken<Type<any>>('GridSettingsDialog');

/**
 * Merges headerClass functions in order to embed first column headers
 */
function mergeHeaderClassFn(decorated: string | string[] | ((params: any) => string | string[] | undefined),
                            decorating: (params: any) => string | string[] | undefined): (params: any) => string | string[] {
  const decoratedFn = isFunction(decorated) ? decorated : constant(decorated);
  return (params: any) => {
    const merged = flatten(over(<any>decorating, (p: any) => decoratedFn(p) ?? [])(params));
    return merged.length ? merged : [];
  };
}

@Injectable()
export class GridDefinitionManager {
  layoutChange = new EventEmitter<GridLayout>();

  private definition: GridDefinition;
  private layout?: ManagedGridLayout;
  private _columns: GridColumnComponent[];

  private api: GridApi;

  private initialized: boolean;
  private firstColId?: string;

  private save$$ = new Subject<void>();
  private destroy$$ = new Subject<void>();

  constructor(private translationService: TranslationService,
              private dialogService: DialogService,
              private gridLayoutService: GridLayoutService,
              @Inject(GRID_SETTINGS_DIALOG) private gridSettingsDialogType: Type<any>) {
    this.onColumnResized = this.onColumnResized.bind(this);
    this.onSortChanged = this.onSortChanged.bind(this);

    this.save$$.pipe(
      takeUntil$(this.destroy$$),
      debounceTime$(SAVE_DEBOUNCE_TIME),
      switchMap$(() => this.gridLayoutService.update(this.layout!.toObject())))
      .subscribe(noop);
  }

  set columns(columns: GridColumnComponent[]) {
    this._columns = columns;
  }

  set pageSize(n: number) {
    if (this.layout) {
      this.layout.merge({ pageSize: n });
      this.save$$.next();
    }
  }

  get pageSize(): number {
    if (this.layout) {
      return this.layout.pageSize;
    } else {
      return DEFAULT_PAGE_SIZE;
    }
  }

  get hasLayout(): boolean {
    return !!this.layout;
  }

  get dynamicLayout(): boolean {
    return !!this.definition;
  }

  createOptions(): GridOptions {
    const { definition } = this;

    const options = definition
      ? this.createDynamicOptions(definition.build())
      : this.createStaticOptions();

    const firstColumnOrGroup = first(options.columnDefs);
    // It is guaranteed that colId is always exist
    this.firstColId = getColumnId(getFirstColumn(firstColumnOrGroup!)!);

    this.normalizeColumnDefs(options);
    return options;
  }

  init(api: GridApi, _columnApi?: GridApi): void {
    this.initialized = true;

    this.api = api;

    if (this.dynamicLayout) {
      api.addEventListener(GRID_EVENT.columnResized, this.onColumnResized);
      api.addEventListener(GRID_EVENT.sortChanged, this.onSortChanged);
    }

    this._columns.forEach((column) => {
      column.setOnEnabled(this.recalculateLayout.bind(this));
      column.setOnVisibilityChange((visible) => {
        this.setColumnVisibility(column.name, visible);
      });
      column.enabled = column.enabled;
    });
  }

  destroy(): void {
    if (this.initialized && this.dynamicLayout) {
      this.api.removeEventListener(GRID_EVENT.columnResized, this.onColumnResized);
      this.api.removeEventListener(GRID_EVENT.sortChanged, this.onSortChanged);
    }

    this.destroy$$.next();
  }

  update(definition: GridDefinition): void {
    const { layout } = <any>definition;

    this.definition = definition;

    this.layout = layout ? ManagedGridLayout.create(layout) : void 0;
  }

  afterGridOptionsEnhanced(options: GridOptions): void {
    this._columns.forEach((column) => column.afterGridOptionsEnhanced(options));
  }

  openSettings(): void {
    const managedLayout = this.layout!;

    this.dialogService.openWithInput(this.gridSettingsDialogType, <GridSettingsDialogConfig>{
      layout: managedLayout.toObject(),
      translate: (name: string) => this.api.getColumnDef(name)?.headerName ?? '',
      onSave: (layout: GridLayout) => this.gridLayoutService.update(layout),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((layout: GridLayout) => {
        managedLayout.update(layout);

        this.applyLayoutChanges(layout);
        this.emitLayoutChange();
      });
  }

  /**
   * This function is necessary for debug purposes only. It allows to generate layout for the current grid.
   */
  generateLayout(name: string): GridLayout {
    if (this.layout) {
      return this.layout.toObject();
    }

    const sortStates = this.api.getColumnState().filter((c) => c.sort != null)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
    const sortDef = first(sortStates);

    let columnPosition = 1;

    return {
      name,
      pageSize: this.pageSize,
      sort: sortDef && sortDef.sort
        ? (() => {
          const direction = fromGridDirection(String(sortDef.sort));
          return direction != null
            ? { field: sortDef.colId, direction }
            : void 0;
        })()
        : void 0,
      columns: this.api.getAllGridColumns().map((column) => {
        const colDef = column.getColDef();
        const columnName = colDef.colId || colDef.field || '';
        return {
          column: columnName,
          position: column.isVisible() ? columnPosition++ : 1,
          width: column.getActualWidth(),
          visible: column.isVisible() || this.isColumnEnabled(columnName),
          resizable: column.isResizable(),
          sortable: toBoolean(colDef.sortable),
        };
      }),
    };
  }

  /**
   * This function is necessary for debug purposes only. It allows to generate MLS labels of this grid.
   */
  generateMlsLabels(name: string): Hash<string> {
// @ts-expect-error Legacy typing suppression
    return fromPairs(this.api.getAllGridColumns().map((column: Column) => {
      const colDef = column.getColDef();
      return [`grid:${name}:${colDef.field || colDef.colId || ''}`, colDef.headerName];
    }));
  }

  private onColumnResized({ source, column }: any): void {
    if (this.layout && source !== 'sizeColumnsToFit' && source !== 'autosizeColumns') {
      this.layout.update(setGridColumnWidth(this.layout.toObject(), column.colId, column.actualWidth));
      const foundItem = find(this.layout.columns, (item) => item.column === column.colId);

      if (foundItem) {
        foundItem.merge({ width: column.actualWidth });
      }

      this.save$$.next();
    }
    this.emitLayoutChange();
  }

  private onSortChanged(event: SortChangedEvent): void {
    this.emitLayoutChange();
  }

  private createDynamicOptions(options: GridOptions): GridOptions {
    const { layout } = this;
    const columnDefs = <ColDef[]>options.columnDefs;

    const isDefaultSortable = !(options.defaultColDef && options.defaultColDef.sortable === false);

    columnDefs.forEach((def) => {
      const colId = (def.colId || def.field)!;
      const foundColumn = this.getColumn(colId);
      if (foundColumn) {
        extend(def, foundColumn.createColDef());
      }
      if (layout) {
        def.headerName = def.headerName || this.getColumnHeaderName(layout.name, colId);
        const column = getGridColumn(layout, colId);
        if (column && (isNil(foundColumn) || foundColumn.enabled && foundColumn.visible)) {
          def.hide = !column.visible;
        }
      }
      def.minWidth = def.minWidth || MIN_COLUMN_WIDTH;
      def.sortable = coalesce(def.sortable, isDefaultSortable);
    });

    return options;
  }

  private createStaticOptions(): GridOptions {
    return {
      columnDefs: this._columns.map((column) => column.createColDef()),
    };
  }

  private applyLayoutChanges(layout: GridLayout): void {
    const visibleColumns = layout.columns.filter(onlyVisible).map(byColumn);
    const enabledColumns = visibleColumns.filter((column) => this.isColumnEnabled(column));
    const disabledColumns = difference(visibleColumns, enabledColumns);
    this.api.setColumnsVisible(enabledColumns, true);
    this.api.moveColumns(enabledColumns, 0);

    const invisibleColumns = layout.columns.filter(onlyInvisible).map(byColumn);
    this.api.setColumnsVisible(invisibleColumns, false);
    this.api.setColumnsVisible(disabledColumns, false);

    this.updateFirstColumnId();

    // CSS styles of first cell differs from other cells. When ag-grid moves column it does not move DOM layers.
    // This way we cannot style first cell of a row when first column was moved.
    // ag-grid does not have easy way to determine whether cell is from the first column or not.
    // Thus we refresh view in order to rebuild all rows and cell and put them in right order.
    this.api.refreshHeader();
    this.api.redrawRows();
  }

  private recalculateLayout(): void {
    this.applyLayoutChanges(this.getOrGenerateLayout());
  }

  /**
   * Finds ID of the first column.
   */
  private updateFirstColumnId(): void {
    const displayedColumns = this.api.getAllDisplayedColumns();
    const firstDisplayedColumn = first(displayedColumns);
    const firstPinnedLeftColumn = first(filter(displayedColumns, (column) => column.getPinned() === 'left'));

    if (firstPinnedLeftColumn) {
      this.firstColId = firstPinnedLeftColumn.getColId();
    } else if (firstDisplayedColumn) {
      this.firstColId = firstDisplayedColumn.getColId();
    } else {
      this.firstColId = void 0;
    }
  }

  /**
   * Normalized column definitions:
   * - merges indents for cells of the first column
   * - guarantees that colId is always exist
   */
  private normalizeColumnDefs(options: GridOptions): void {
    const isFirstCellClass = ({ colDef: { colId } }: any) => colId === this.firstColId;
    const getFirstHeaderCellClass = (params: any) => isFirstCellClass(params) ? CELL_FIRST_COLUMN_CLASS : [];
    forEachColDef(options.columnDefs || [], (colDef: ColDef) => {
      const originalHeaderClass = colDef.headerClass;
      colDef.colId = getColumnId(colDef);
      colDef.headerClass = originalHeaderClass
        ? mergeHeaderClassFn(originalHeaderClass, getFirstHeaderCellClass)
        : getFirstHeaderCellClass;
      colDef.cellClassRules = {
        ...(colDef.cellClassRules || {}),
        [CELL_FIRST_COLUMN_CLASS]: isFirstCellClass,
      };
    });
  }

  private emitLayoutChange(): void {
    const newLayout = this.getOrGenerateLayout();

    this.layoutChange.emit(newLayout);
  }

  private isColumnEnabled(name: string): boolean {
    const column = this.getColumn(name);
    return column ? column.enabled : true;
  }

  private getColumn(name: string): Maybe<GridColumnComponent> {
    return this._columns.find((column) => column.name === name);
  }

  private getOrGenerateLayout(): GridLayout {
    const name = this.layout ? this.layout.name : 'generated';
    return this.generateLayout(name);
  }

  private getColumnHeaderLabel(layoutName: string, columnName: string): string {
    const regionalHeaderKey = `grid:${layoutName}:${columnName}{${REGION_CODE}}`;
    if (this.translationService.getRaw(regionalHeaderKey)) {
      return this.translationService.get(regionalHeaderKey);
    }
    return this.translationService.get(`grid:${layoutName}:${columnName}`);
  }

  private getColumnHeaderName(layoutName: string, columnName: string): string {
    const label = this.getColumnHeaderLabel(layoutName, columnName);
    return this.translationService.get(label);
  }

  private setColumnVisibility(name: string, visible: boolean): void {
    const newLayout = setGridColumnVisibility(this.getOrGenerateLayout(), name, visible);
    if (this.layout) {
      this.layout.update(newLayout);
    }
    this.applyLayoutChanges(newLayout);
    this.save$$.next();
  }
}
